import clsx from "clsx";
import type { KeyboardEvent } from "react";
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

interface HourMark {
	label: string;
	value: number;
}

type SharedPropsType = {
	hourMarks: HourMark[];
	label?: string;
	error?: string;
};

type DurationSliderPlainProps = SharedPropsType & {
	name?: string;
	value?: number;
	onChange?: (value: number) => void;
	onBlur?: () => void;
};

export const DurationSliderPlain = forwardRef<HTMLDivElement, DurationSliderPlainProps>(
	({ hourMarks, label, value = 0, onChange, onBlur }, forwardedRef) => {
		const [isDragging, setIsDragging] = useState(false);
		const [internalValue, setInternalValue] = useState(value);
		const internalRef = useRef<HTMLDivElement | null>(null);

		const sliderRef = useCallback(
			(node: HTMLDivElement | null) => {
				internalRef.current = node;
				if (typeof forwardedRef === "function") {
					forwardedRef(node);
				} else if (forwardedRef) {
					forwardedRef.current = node;
				}
			},
			[forwardedRef],
		);

		useEffect(() => {
			const maxValue = hourMarks[hourMarks.length - 1].value;
			const sanitisedValue = Math.min(Math.max(value, 0), maxValue);

			const hours = Math.floor(sanitisedValue / 60);
			const minutes = Math.round(sanitisedValue % 60);
			const quarterHour = Math.ceil(minutes / 15) * 15;
			setInternalValue(hours * 60 + quarterHour);
		}, [hourMarks, value]);

		const getNearestMarkValue = useCallback((percentage: number): number => {
			const maxValue = hourMarks[hourMarks.length - 1].value;
			const exactValue = percentage * maxValue;
			const stepSize = 15; // 15 minutes step

			return Math.round(exactValue / stepSize) * stepSize;
		}, [hourMarks]);

		const updateInternalValue = useCallback((clientX: number) => {
			const sliderElement = internalRef.current;
			if (sliderElement) {
				const rect = sliderElement.getBoundingClientRect();
				const position = Math.max(0, Math.min(clientX - rect.left, rect.width));
				const percentage = position / rect.width;
				const newValue = getNearestMarkValue(percentage);
				setInternalValue(newValue);
				if (onChange) {
					onChange(newValue);
				}
			}
		}, [getNearestMarkValue, onChange]);

		const handlePointerDown = useCallback((e: React.PointerEvent) => {
			e.preventDefault(); // Prevent text selection
			setIsDragging(true);
			updateInternalValue(e.clientX);
		}, [updateInternalValue]);

		const handlePointerMove = useCallback((e: PointerEvent) => {
			if (isDragging) {
				updateInternalValue(e.clientX);
			}
		}, [isDragging, updateInternalValue]);

		const handlePointerUp = useCallback(() => {
			setIsDragging(false);
		}, []);

		useEffect(() => {
			if (isDragging) {
				document.addEventListener('pointermove', handlePointerMove);
				document.addEventListener('pointerup', handlePointerUp);
				document.addEventListener('pointercancel', handlePointerUp);
			}

			return () => {
				document.removeEventListener('pointermove', handlePointerMove);
				document.removeEventListener('pointerup', handlePointerUp);
				document.removeEventListener('pointercancel', handlePointerUp);
			};
		}, [isDragging, handlePointerMove, handlePointerUp]);

		const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
			const step = 15; // 15 minutes step
			let newValue = internalValue;

			switch (e.key) {
				case "ArrowLeft":
					newValue = Math.max(0, internalValue - step);
					break;
				case "ArrowRight":
					newValue = Math.min(internalValue + step, hourMarks[hourMarks.length - 1].value);
					break;
				default:
					return;
			}

			if (newValue !== internalValue) {
				setInternalValue(newValue);
				onChange && onChange(Math.round(newValue));
			}
		};

		const getLeftPosition = (value: number) => {
			const maxValue = hourMarks[hourMarks.length - 1].value;
			return `${(value / maxValue) * 100}%`;
		};

		const getRightPosition = (value: number) => {
			const maxValue = hourMarks[hourMarks.length - 1].value;
			return `${100 - (value / maxValue) * 100}%`;
		};

		const formatTime = (value: number) => {
			const hours = Math.floor(value / 60);
			const minutes = Math.round(value % 60);
			return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
		};

		return (
			<div className="relative w-full select-none">
				{label && <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>}
				<div
					className={clsx("group relative h-20 w-full touch-none focus:outline-none focus:ring-0",
						isDragging ? "cursor-grabbing" : "cursor-pointer")}
					ref={sliderRef}
					onPointerDown={handlePointerDown}
					tabIndex={0}
					onKeyDown={handleKeyDown}
					onBlur={onBlur}
				>
					<div className="absolute top-1/2 h-2 w-full -translate-y-1/2 rounded-lg bg-gray-300" />
					<div className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-lg bg-primary-500"
						 style={{ right: getRightPosition(internalValue) }} />
					{hourMarks.map((mark, index) => {
						const leftPosition = getLeftPosition(mark.value);

						return <React.Fragment key={index}>
							<div className={clsx(
								"absolute bottom-0 -translate-x-1/2 whitespace-nowrap text-xs tracking-tighter",
								mark.value <= Math.floor(internalValue) ? "font-bold text-gray-600" : "text-gray-400",
							)}
								 style={{ left: leftPosition }}
							>
								{mark.label} h
							</div>
						</React.Fragment>;
					})}
					<div
						className="absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-500 shadow ring-4 ring-white group-focus:border-2 group-focus:border-primary-300"
						style={{ left: getLeftPosition(internalValue) }}
					>
						<div className={clsx("absolute -top-full left-1/2 -mt-1 -translate-x-1/2 text-xs font-bold text-gray-500")}>
							{formatTime(internalValue)}
						</div>
					</div>
				</div>
			</div>
		);
	},
);

DurationSliderPlain.displayName = "DurationSliderPlain";

type DurationSliderProps = SharedPropsType & {
	name: string;
	control: Control<any>;
};

const DurationSlider: React.FC<DurationSliderProps> = ({ name, control, hourMarks, label, ...rest }) => {
	const {
		field: { ref, value, onChange, ...inputProps },
		fieldState: { error },
	} = useController({
		name,
		control,
	});

	return (
		<DurationSliderPlain
			ref={ref}
			value={value}
			onChange={(newValue) => onChange(newValue)}
			{...inputProps}
			{...rest}
			hourMarks={hourMarks}
			label={label}
			error={error?.message}
		/>
	);
};

export default DurationSlider;